import IReportResponse from '@/report/models/IReportResponse';
import Axios from 'axios';
import { computed, ComputedRef, defineComponent, ref } from 'vue';
import { useStore } from 'vuex';
import LogHelper from '@/generic/helpers/LogHelper';
import EnvService from '@/generic/services/EnvService';
import fileDownload from 'js-file-download';
import $ from 'jquery';
import AuthHelper, { TokenType } from '@/generic/helpers/AuthHelper';
import Language from '@/store/models/Language';
import { domToImage } from 'modern-screenshot';
import { applyCtaOverlayShadowFix } from '@/report/cta/helpers/ctaOverlayRasterFix';
import { rasterizeCtaSvgImages } from '@/report/cta/helpers/ctaSvgRasterFix';
import { applyCtaDomainPrintLayout } from '@/report/cta/helpers/ctaDomainPrintLayout';

export default defineComponent({
    setup() {
        const loading = ref(false);
        const store = useStore();
        const report: ComputedRef<IReportResponse> = computed(() => store.getters['report/report']);
        const isTeam: ComputedRef<boolean> = computed(() => store.getters['report/isTeam']);
        //TODO: report alias is now used to hide print bar, make this a setting in the future
        const reportAlias: ComputedRef<string> = computed(() => store.getters['report/alias']);
        const participantName: ComputedRef<string> = computed(() => {
            return report.value.participantName ? report.value.participantName.replace(' ', '_') : 'N/A';
        });
        const filename: ComputedRef<string> = computed(() => {
            const parts = [participantName.value, report.value.instrumentAlias];
            const name = parts.filter((part) => part !== '' && part != undefined && part != 'N/A').join('_');
            if (isTeam.value) {
                return `Team_${name ? name + '_' : ''}Report.pdf`;
            }
            return `${name}_Report.pdf`;
        });
        const pitViewSelector = computed(() => (reportAlias.value === 'pit' ? document.getElementById('view-selector') : null));

        const canvasToImg = () => {
            document.querySelectorAll('canvas').forEach((c) => {
                const imgSrc = c.toDataURL('image/png');
                const img = document.createElement('img');
                img.style.width = c.clientWidth + 'px';
                img.style.height = c.clientHeight + 'px';
                img.src = imgSrc;
                c.parentNode.insertBefore(img, c);
                c.parentNode.removeChild(c);
            });
        };

        /**
         * Append selected attribute, to make sure
         * the selected option is visible in the pdf
         */
        const appendSelectedAttribute = () => {
            document.querySelectorAll('option').forEach((option) => {
                if (option.selected) {
                    option.setAttribute('selected', 'selected');
                }
            });
        };

        const removeSelectedAttribute = () => {
            document.querySelectorAll('option').forEach((option) => option.removeAttribute('selected'));
        };

        const preRender = () => {
            canvasToImg();
            document.querySelector('body').setAttribute('class', 'rendering');
            appendSelectedAttribute();
        };

        const afterRender = () => {
            removeCustomPitHtmlFixes();
            removeCustomCtaHtmlFixes();

            document.querySelector('body').removeAttribute('class');
            removeSelectedAttribute();
        };

        const doCustomBaselineHtmlFixes = () => {
            $('.page:not(:last)').attr('style', 'page-break-after: always;');

            $('.row').removeClass('row').addClass('best-row');
            $("[class^='col']").each(function (index, element) {
                const numbers = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10', '11', '12'],
                    letters = ['xs', 'sm', 'md', 'lg', 'xl'];
                const $element = $(element);

                for (let i = 0; i < numbers.length; i++) {
                    for (let j = 0; j < letters.length; j++) {
                        if ($element.hasClass('col-' + numbers[i])) {
                            $element.removeClass('col-' + numbers[i]).addClass('best-col-' + numbers[i]);
                        } else if ($element.hasClass('col-' + letters[j] + '-' + numbers[i])) {
                            $element.removeClass('col-' + letters[j] + '-' + numbers[i]).addClass('best-col-' + letters[j] + '-' + numbers[i]);
                        }
                    }
                }
            });

            $('.stuck-zone, .over-use-zone, .strong-zone, .weak-zone').each(function () {
                const $el = $(this);
                $el.height($el.height()).css('bottom', $el.css('bottom'));
            });
        };

        const doCustomPitHtmlFixes = async () => {
            const node = document.getElementById('chart-wrapper');
            if (reportAlias.value === 'pit') {
                document.getElementById('main-layout-content').removeChild(pitViewSelector.value);
            }

            await domToImage(node)
                .then((img) => {
                    img.setAttribute('id', 'wheel-export-image');
                    node.appendChild(img);
                })
                .catch((error) => {
                    console.error('oops, something went wrong!', error);
                });
        };

        const removeCustomPitHtmlFixes = () => {
            if (
                report.value.knowledgeModelId === 12 ||
                report.value.instrumentAlias === 'pit-test' ||
                report.value.knowledgeModelId === 9 ||
                report.value.instrumentAlias === 'pit-test-correct' ||
                (isTeam.value && reportAlias.value === 'pit')
            ) {
                document.getElementById('wheel-export-image').remove();
                if (reportAlias.value === 'pit') {
                    document.getElementById('main-layout-content').prepend(pitViewSelector.value);
                }
            }
        };

        const doCustomCTAHtmlFixes = async () => {
            const chartElements = document.querySelectorAll('.cta-chart-container');
            for (let i = 0; i < chartElements.length; i++) {
                const chart = chartElements[i] as HTMLElement;
                // scale renders the source at a higher pixel density than the container's CSS
                // size (domToImage defaults to scale 1, i.e. one raster pixel per CSS pixel),
                // so the PDF renderer isn't stretching an under-sized bitmap. The explicit
                // width/height keep the <img> at the chart's original layout size regardless
                // of scale, matching the sizing canvasToImg() already does for <canvas> above.
                //
                // Neutralizes the overlay's two shadow sources for the capture and restores
                // them afterwards - see ctaOverlayRasterFix.ts for why both are needed.
                const shadowFix = await applyCtaOverlayShadowFix(chart);

                try {
                    const img = await domToImage(chart, { scale: 2 });
                    img.setAttribute('class', 'cta-chart-export-image');
                    img.style.width = `${chart.clientWidth}px`;
                    img.style.height = `${chart.clientHeight}px`;
                    chart.appendChild(img);
                    chart.querySelectorAll('[id^="chart-"]').forEach((c) => ((c as HTMLElement).style.display = 'none'));
                } catch (error) {
                    console.error('oops, something went wrong!', error);
                } finally {
                    shadowFix.restore();
                }
            }

            document.querySelectorAll('.value-label').forEach((label) => ((label as HTMLElement).style.display = 'none'));
            document.querySelectorAll('.overlay-big, .overlay').forEach((overlay) => ((overlay as HTMLElement).style.display = 'none'));
            document.querySelectorAll('[class^="overlay-text-"]').forEach((overlay) => ((overlay as HTMLElement).style.display = 'none'));
        };

        const removeCustomCtaHtmlFixes = () => {
            if (report.value.instrumentAlias && report.value.instrumentAlias.indexOf('cta') !== -1) {
                document.querySelectorAll('.cta-chart-export-image').forEach((img) => img.remove());
                document.querySelectorAll('[id^="chart-"]').forEach((c) => ((c as HTMLElement).style.display = 'block'));
                document.querySelectorAll('.value-label').forEach((label) => ((label as HTMLElement).style.display = 'block'));
                document.querySelectorAll('.overlay-big, .overlay').forEach((overlay) => ((overlay as HTMLElement).style.display = 'block'));
                document.querySelectorAll('[class^="overlay-text-"]').forEach((overlay) => ((overlay as HTMLElement).style.display = 'block'));
            }
        };

        const getPrintHtml = async () => {
            preRender();
            loading.value = false;
            if (report.value.instrumentAlias && report.value.instrumentAlias.indexOf('cta') !== -1) {
                await doCustomCTAHtmlFixes();
            }

            if (report.value.instrumentAlias === 'baseline') {
                doCustomBaselineHtmlFixes();
            }

            if (
                report.value.knowledgeModelId === 12 ||
                report.value.instrumentAlias === 'pit-test' ||
                report.value.knowledgeModelId === 9 ||
                report.value.instrumentAlias === 'pit-test-correct' ||
                (isTeam.value && reportAlias.value === 'pit')
            ) {
                await doCustomPitHtmlFixes();
            }

            const html = document.querySelector('html');
            html.querySelectorAll('noscript').forEach((noscript) => noscript.remove());
            html.querySelectorAll('.iteration-picker-wrapper').forEach((noscript) => (noscript.className += ' hidden'));
            html.querySelectorAll('#demographic-selector').forEach((noscript) => (noscript.className += ' hidden'));
            const exportHtml = html.cloneNode(true) as HTMLElement;
            if (report.value.instrumentAlias && report.value.instrumentAlias.indexOf('cta') !== -1) {
                await rasterizeCtaSvgImages(html, exportHtml);
                applyCtaDomainPrintLayout(exportHtml);
            }
            return exportHtml.innerHTML;
        };

        const handlePrint = () => {
            loading.value = true;

            setTimeout(() => {
                print();
            }, 1000);
        };

        const print = async () => {
            try {
                const data = new URLSearchParams();
                const htmlData = await getPrintHtml();
                data.append('html', htmlData);
                data.append('ParticipantName', participantName.value);
                const language: Language = store.getters.language;
                data.append('CopyrightName', `(C) ${language.knowledgeModelName}`);
                data.append('DocumentLanguage', language.languageCode);

                loading.value = true;
                const env = EnvService.getEnvironment();
                const token = isTeam.value ? TokenType.ReportTeamToken : TokenType.SurveyToken;
                const jwt = AuthHelper.token(token);
                const response = await Axios({
                    url: `${env.reportApiEndpoint}/reports/pdf`,
                    method: 'post',
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded;charset=utf-8',
                        Authorization: isTeam.value ? `TeamToken ${jwt}` : `BrightInstruments ${jwt}`,
                    },
                    responseType: 'blob',
                    data: data,
                });
                // TODO: Add expose content-disposition header in web.config
                const disposition = response.headers['content-disposition'];
                let dispositionFilename: string;
                LogHelper.info('Print disposition header', { disposition, headers: response.headers });
                if (disposition && disposition.indexOf('attachment') !== -1) {
                    const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
                    const matches = filenameRegex.exec(disposition);
                    if (matches != null && matches[1]) {
                        dispositionFilename = matches[1].replace(/['"]/g, '');
                    }
                }
                fileDownload(response.data, dispositionFilename === '' ? dispositionFilename : filename.value, 'application/pdf;charset=utf-8');
            } catch (e) {
                LogHelper.error('Could not download file', e);
            } finally {
                const html = document.querySelector('html');
                html.querySelectorAll('.iteration-picker-wrapper').forEach((noscript) => (noscript.className = 'iteration-picker-wrapper'));
                html.querySelectorAll('#demographic-selector').forEach((noscript) => (noscript.className = 'survey-select'));

                afterRender();
                loading.value = false;
            }
        };

        return {
            print,
            handlePrint,
            loading,
            reportAlias,
        };
    },
});
